import { useState, useMemo, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Plus, Search, ChevronDown, Check,
  MoreHorizontal, Trash2, ArrowUpDown, FolderOpen, X, User, CalendarDays,
  CalendarClock, ArrowRight, Lock, Link2, CornerDownRight, Layers, ChevronRight, Unlink, Network, LayoutGrid,
} from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSession } from '@/context/AuthContext'
import { useApp } from '@/context/AppContext'
import { CreateProjectModal } from '@/components/CreateProjectModal'
import { ProjectDiagram } from '@/components/ProjectDiagram'
import { useClickOutside } from '@/lib/useClickOutside'
import { useRegisteredUsers } from '@/lib/useRegisteredUsers'
import { formatDate, picColor, parsePics, isPic } from '@/lib/utils'
import { canModifyProject, canDeleteProject, hasCrossDeptView, isDirector } from '@/lib/roles'
import { deptColor, DEPARTMENTS, INTERNAL_DIVISIONS, BUSINESS_UNITS } from '@/lib/departments'
import type { Project } from '@/lib/types'

// ─── types ───────────────────────────────────────────────────────────────────
type SortKey = 'recent' | 'name' | 'progress' | 'tasks'
type FilterStatus = 'all' | 'active' | 'completed' | 'not-started'

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'recent', label: 'Terbaru' },
  { key: 'name', label: 'Nama A–Z' },
  { key: 'progress', label: 'Progress' },
  { key: 'tasks', label: 'Jumlah Task' },
]
const FILTER_TABS: { key: FilterStatus; label: string }[] = [
  { key: 'all', label: 'Semua' },
  { key: 'active', label: 'Aktif' },
  { key: 'completed', label: 'Selesai' },
  { key: 'not-started', label: 'Belum Mulai' },
]
function PicAvatar({ name, size = 'sm' }: { name: string; size?: 'sm' | 'md' }) {
  const bg = picColor(name)
  const cls = size === 'md' ? 'w-7 h-7 text-xs' : 'w-5 h-5 text-[9px]'
  return (
    <div className={`${cls} rounded-full flex items-center justify-center font-body font-bold text-white flex-shrink-0`}
      style={{ backgroundColor: bg }} title={name}>
      {name.charAt(0).toUpperCase()}
    </div>
  )
}

// ─── Collaborator avatar stack (maks 3 + sisanya "+N") ────────────────────────
function CollaboratorStack({ names }: { names: string[] }) {
  if (names.length === 0) return null
  const shown = names.slice(0, 3)
  const extra = names.length - shown.length
  return (
    <div className="flex items-center -space-x-1.5 flex-shrink-0" title={`Kolaborator: ${names.join(', ')}`} aria-label={`Kolaborator: ${names.join(', ')}`}>
      {shown.map((n) => (
        <div key={n}
          className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-body font-bold text-white ring-2 ring-white"
          style={{ backgroundColor: picColor(n) }} title={n}>
          {n.charAt(0).toUpperCase()}
        </div>
      ))}
      {extra > 0 && (
        <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-body font-bold text-[#6B6860] bg-[#EDECE7] ring-2 ring-white">
          +{extra}
        </div>
      )}
    </div>
  )
}

// ─── Compact donut (72px) ─────────────────────────────────────────────────────
function DonutChart({ done, inProgress, notStarted }: { done: number; inProgress: number; notStarted: number }) {
  const total = done + inProgress + notStarted
  const pctDone  = total > 0 ? (done / total) * 100 : 0
  const pctActive = total > 0 ? (inProgress / total) * 100 : 0
  return (
    <div className="relative w-[72px] h-[72px] flex-shrink-0">
      <div className="w-full h-full rounded-full" style={{
        background: total === 0 ? '#F0EFE9'
          : `conic-gradient(#16A34A 0% ${pctDone}%, #F59E0B ${pctDone}% ${pctDone + pctActive}%, #E2E8F0 ${pctDone + pctActive}% 100%)`,
      }} />
      <div className="absolute inset-[13px] rounded-full bg-white flex flex-col items-center justify-center">
        <span className="font-display text-[14px] font-bold leading-none" style={{ color: '#16A34A' }}>
          {Math.round(pctDone)}%
        </span>
        <span className="text-[7px] text-[#AEADA8] font-body tracking-widest uppercase mt-0.5">done</span>
      </div>
    </div>
  )
}

// ─── Stacked bar ─────────────────────────────────────────────────────────────
function TaskBar({ done, inProgress, notStarted }: { done: number; inProgress: number; notStarted: number }) {
  const total = done + inProgress + notStarted
  if (total === 0) return <div className="h-1.5 w-full bg-[#EEEEE9] rounded-full" />
  const pD = (done / total) * 100, pA = (inProgress / total) * 100, pT = (notStarted / total) * 100
  return (
    <div className="flex h-1.5 rounded-full overflow-hidden w-full gap-px">
      {pD > 0 && <div style={{ width: `${pD}%`, backgroundColor: '#16A34A' }} />}
      {pA > 0 && <div style={{ width: `${pA}%`, backgroundColor: '#F59E0B' }} />}
      {pT > 0 && <div style={{ width: `${pT}%`, backgroundColor: '#E2E8F0' }} />}
    </div>
  )
}

// ─── Health pill ─────────────────────────────────────────────────────────────
function HealthPill({ pct, total, inProgress = 0 }: { pct: number; total: number; inProgress?: number }) {
  if (total === 0) return <span className="text-[10px] font-body text-[#C0BDB6]">Kosong</span>
  if (pct === 100) return <span className="text-[10px] font-body font-semibold text-white bg-[#16A34A] px-1.5 py-0.5 rounded-full">✓ Selesai</span>
  if (pct >= 60)  return <span className="text-[10px] font-body font-semibold text-[#15803D] bg-[#DCFCE7] border border-[#BBF7D0] px-1.5 py-0.5 rounded-full">On Track</span>
  // Sudah ada task yang dikerjakan (done sebagian ATAU ada task in-progress) → In Progress
  if (pct > 0 || inProgress > 0) return <span className="text-[10px] font-body font-semibold text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] px-1.5 py-0.5 rounded-full">In Progress</span>
  return <span className="text-[10px] font-body text-[#94A3B8] bg-[#F1F5F9] border border-[#E2E8F0] px-1.5 py-0.5 rounded-full">Belum Mulai</span>
}

// Dropdown pilih departemen tujuan delegasi (khusus Director).
function AssignDeptMenu({
  current,
  ownerDept,
  onPick,
}: {
  current: string | null
  ownerDept: string | null | undefined
  onPick: (dep: string | null) => void
}) {
  const options = DEPARTMENTS.filter((d) => d !== ownerDept)
  return (
    <div className="absolute left-0 top-7 z-30 bg-white border border-[#E8E7E3] rounded-xl shadow-xl py-1.5 w-44 animate-fade-in">
      <p className="px-3 py-1 text-[9px] uppercase tracking-wider text-[#AEADA8] font-body font-semibold">Tugaskan ke</p>
      {options.map((d) => {
        const c = deptColor(d)
        return (
          <button
            key={d}
            type="button"
            onClick={() => onPick(d)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-body transition-colors ${
              d === current ? 'bg-[#F4F3F0] text-[#1A1916] font-semibold' : 'text-[#6B6860] hover:bg-[#F4F3F0]'
            }`}
          >
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: c.text }} />
            <span className="flex-1 text-left truncate">{d}</span>
            {d === current && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
          </button>
        )
      })}
      {current && (
        <>
          <div className="my-1 border-t border-[#F2F1EC]" />
          <button
            type="button"
            onClick={() => onPick(null)}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-body text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
          >
            <X className="w-3.5 h-3.5 flex-shrink-0" /> Lepas penugasan
          </button>
        </>
      )}
    </div>
  )
}

// ─── Project card (grid) ──────────────────────────────────────────────────────
function ProjectCard({ project }: { project: Project }) {
  const { projects, getProjectTasks, getTaskSubtasks, deleteProject, getEffectiveTaskStatus, updateProject, applyProject, getProjectLock, getProjectOrder } = useApp()
  const navigate = useNavigate()
  // Wewenang & badge departemen
  const { data: session } = useSession()
  const viewer = { role: session?.user?.role, department: session?.user?.department, email: session?.user?.email, username: session?.user?.username }
  const canModify = canModifyProject(viewer, {
    department: project.department,
    assignedDepartment: project.assignedDepartment,
    ownerEmail: project.createdByEmail,
    pic: project.pic,
  })
  // Hapus memakai gate yang lebih sempit, selaras server — kalau dipakai `canModify`,
  // tombol Hapus muncul untuk orang sedepartemen/Holding yang requestnya akan ditolak 403.
  const canDelete = canDeleteProject(viewer, {
    department: project.department,
    ownerEmail: project.createdByEmail,
    pic: project.pic,
  })
  const crossDept = !!project.department && project.department !== (session?.user?.department ?? null)
  const dc = deptColor(project.department)
  // Delegasi project (HOLDING → dept lain). Kontrol khusus Director (mirror server).
  const holding = hasCrossDeptView(viewer)
  const canAssignDept = isDirector(viewer.role) && project.department === 'HOLDING'
  const assignedDept = project.assignedDepartment ?? null
  const adc = deptColor(assignedDept)
  const [showAssign, setShowAssign] = useState(false)
  const assignRef = useRef<HTMLDivElement>(null)
  useClickOutside(assignRef, () => setShowAssign(false), showAssign)
  const assignDept = async (dep: string | null) => {
    setShowAssign(false)
    try {
      const res = await fetch(`/api/projects/${project.id}/assign-department`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ department: dep }),
      })
      const data = await res.json()
      if (res.ok && data?.id) applyProject(data as Project)
    } catch { /* abaikan */ }
  }
  const [showMenu, setShowMenu] = useState(false)
  const locked = getProjectLock(project.id).locked
  const order = getProjectOrder(project.id)
  const [editingDate, setEditingDate] = useState(false)
  const [editingDep, setEditingDep] = useState(false)
  const blocker = project.dependsOn ? projects.find((p) => p.id === project.dependsOn) ?? null : null
  // Project yang bergantung pada ini (dikerjakan setelah project ini selesai)
  const dependents = projects.filter((p) => p.dependsOn === project.id)
  const saveDep = (value: string) => {
    updateProject(project.id, { dependsOn: value || null })
    setEditingDep(false)
  }
  const menuRef = useRef<HTMLDivElement>(null)
  useClickOutside(menuRef, () => setShowMenu(false), showMenu)
  const tasks = getProjectTasks(project.id)
  // Task RUTIN dikecualikan dari perhitungan progres (di luar %).
  const countTasks = tasks.filter(t => !t.recurrence)
  const total = countTasks.length
  const done = countTasks.filter(t => getEffectiveTaskStatus(t.id) === 'done').length
  const inProgress = countTasks.filter(t => getEffectiveTaskStatus(t.id) === 'in-progress').length
  const notStarted = countTasks.filter(t => getEffectiveTaskStatus(t.id) === 'not-started').length
  const subCount = tasks.reduce((a, t) => a + getTaskSubtasks(t.id).length, 0)
  const pct = total > 0 ? Math.round((done / total) * 100) : 0

  // Kolaborator diambil dari assignee task + subtask. Kalau sama sekali tidak ada
  // assignee di task/subtask → kembalikan kosong (stack disembunyikan), meski ada PIC.
  // Kalau ada → tampilkan PIC (owner) + para assignee sebagai tim, dedupe case-insensitive.
  const collaborators = (() => {
    const norm = (n?: string | null) => (n ?? '').trim()
    const work = new Map<string, string>()
    const addWork = (n?: string | null) => { const v = norm(n); if (v && !work.has(v.toLowerCase())) work.set(v.toLowerCase(), v) }
    // PIC task & subtask sama-sama daftar nama digabung ';' — harus dipecah,
    // kalau tidak 'Ana;Budi' terhitung sebagai satu orang bernama 'Ana;Budi'.
    for (const t of tasks) {
      for (const n of parsePics(t.assignee)) addWork(n)
      for (const s of getTaskSubtasks(t.id)) for (const n of parsePics(s.assignee)) addWork(n)
    }
    if (work.size === 0) return []
    const all = new Map<string, string>()
    const pic = norm(project.pic)
    if (pic) all.set(pic.toLowerCase(), pic)
    for (const [k, v] of work) if (!all.has(k)) all.set(k, v)
    return Array.from(all.values())
  })()

  const dl = project.deadline ? (() => {
    const d = new Date(project.deadline)
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const dm = new Date(d); dm.setHours(0, 0, 0, 0)
    const diff = Math.round((dm.getTime() - today.getTime()) / 86400000)
    return {
      label: d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }),
      overdue: diff < 0 && pct < 100,
      soon: diff >= 0 && diff <= 3 && pct < 100,
    }
  })() : null

  const saveDeadline = (value: string) => {
    updateProject(project.id, { deadline: value || null })
    setEditingDate(false)
  }

  return (
    <div
      onClick={() => navigate(`/projects/${project.id}`)}
      className="group relative bg-white border border-[#E8E7E3] rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-200 cursor-pointer"
    >
      <div className="h-[3px] rounded-t-xl" style={{ backgroundColor: project.color }} />
      <div className="p-4">
        {/* name + menu */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-display text-[15px] font-bold text-[#1A1916] flex-1 min-w-0 flex items-start gap-1.5 leading-snug">
            {order != null && (
              <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 mt-0.5 rounded-md bg-[#1A1916] text-white text-[10px] font-bold" title={`Urutan ke-${order} di rantai`}>
                {order}
              </span>
            )}
            {locked && <Lock className="w-3 h-3 text-[#B45309] flex-shrink-0 mt-1" />}
            <span className="break-words">{project.name}</span>
          </h3>
          {collaborators.length > 0 && <div className="mt-0.5"><CollaboratorStack names={collaborators} /></div>}
          {canDelete && (
            <div ref={menuRef} className="relative flex-shrink-0">
              <button onClick={e => { e.stopPropagation(); setShowMenu(!showMenu) }}
                className={`p-1 rounded-lg text-[#C8C7BF] hover:text-[#6B6860] hover:bg-[#F4F3F0] transition-colors ${showMenu ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-7 z-30 bg-white border border-[#E8E7E3] rounded-xl shadow-xl py-1.5 w-40 animate-fade-in">
                  <button onClick={e => { e.stopPropagation(); deleteProject(project.id); setShowMenu(false) }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#DC2626] hover:bg-[#FEF2F2] font-body transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Hapus
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Badge departemen / delegasi — baris sendiri agar tak menutupi judul. */}
        {(assignedDept || canAssignDept || crossDept) && (
          <div className="mb-2.5">
            {assignedDept ? (
              // Sudah di-assign → badge "HOLDING × <dept>" (klik utk ubah/lepas bila Director).
              <div ref={assignRef} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  disabled={!canAssignDept}
                  onClick={() => canAssignDept && setShowAssign((s) => !s)}
                  title={canAssignDept ? 'Ubah / lepas penugasan departemen' : `Delegasi: ${project.department} × ${assignedDept}`}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-body font-bold uppercase tracking-wider border ${canAssignDept ? 'cursor-pointer hover:brightness-95' : 'cursor-default'}`}
                  style={{ backgroundColor: adc.bg, color: adc.text, borderColor: adc.border }}
                >
                  {project.department} <span className="opacity-60">×</span> {assignedDept}
                </button>
                {canAssignDept && showAssign && (
                  <AssignDeptMenu current={assignedDept} ownerDept={project.department} onPick={assignDept} />
                )}
              </div>
            ) : canAssignDept ? (
              // Belum di-assign & viewer Director → tombol assign.
              <div ref={assignRef} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => setShowAssign((s) => !s)}
                  title="Tugaskan project ke departemen lain"
                  className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-body font-bold uppercase tracking-wider border border-dashed border-[#C8C7BF] text-[#8A8A85] hover:border-[#1A1916] hover:text-[#1A1916] transition-colors"
                >
                  <Plus className="w-2.5 h-2.5" /> Assign ke Dept
                </button>
                {showAssign && (
                  <AssignDeptMenu current={null} ownerDept={project.department} onPick={assignDept} />
                )}
              </div>
            ) : crossDept ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-body font-bold uppercase tracking-wider border"
                style={{ backgroundColor: dc.bg, color: dc.text, borderColor: dc.border }}
                title={`Departemen: ${project.department}`}>
                {project.department}
              </span>
            ) : null}
          </div>
        )}

        {/* description — 1 line */}
        <p className="text-[12px] text-[#8A8A85] font-body truncate mb-3">
          {project.description || '—'}
        </p>

        {/* progress */}
        <div className="mb-2.5">
          <div className="flex justify-between mb-1">
            <span className="text-[9px] uppercase tracking-wider text-[#C0BDB6] font-body">Progress</span>
            <span className="text-[10px] font-body font-semibold text-[#6B6860]">{pct}%</span>
          </div>
          <TaskBar done={done} inProgress={inProgress} notStarted={notStarted} />
        </div>

        {/* legend compact */}
        <div className="flex items-center gap-2.5 text-[10px] font-body mb-3">
          <span className="text-[#15803D] font-semibold flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-sm bg-[#16A34A] inline-block" />{done}
          </span>
          <span className="text-[#B45309] font-semibold flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-sm bg-[#F59E0B] inline-block" />{inProgress}
          </span>
          <span className="text-[#94A3B8] flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-sm bg-[#E2E8F0] inline-block" />{notStarted}
          </span>
          <span className="ml-auto flex items-center gap-1 text-[#C0BDB6]" title={`${total} task · ${subCount} subtask`}>
            <Layers className="w-3 h-3" />{subCount} subtask
          </span>
        </div>

        {/* deadline + open arrow */}
        <div className="flex items-center justify-between gap-2 mb-2.5">
          {editingDate ? (
            <input
              type="date"
              autoFocus
              defaultValue={project.deadline?.slice(0, 10) ?? ''}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => { e.stopPropagation(); saveDeadline(e.target.value) }}
              onBlur={() => setEditingDate(false)}
              className="text-[10px] px-2 py-1 bg-[#F8F7F3] border border-[#C8C7BF] rounded-lg font-body text-[#1A1916] focus:outline-none focus:border-[#1A1916]"
            />
          ) : dl ? (
            <button
              onClick={(e) => { e.stopPropagation(); if (canModify) setEditingDate(true) }}
              className={`flex items-center gap-1 text-[10px] font-body font-medium px-2 py-1 rounded-lg transition-colors ${
                dl.overdue ? 'bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]'
                : dl.soon ? 'bg-[#FEF3C7] text-[#B45309] border border-[#FDE68A]'
                : 'bg-[#F4F3F0] text-[#6B6860] hover:bg-[#EEEEE9]'
              } ${canModify ? '' : 'cursor-default'}`}
              title={canModify ? 'Klik untuk ubah deadline' : undefined}
            >
              <CalendarClock className="w-3 h-3" />
              {dl.overdue ? `Telat · ${dl.label}` : dl.label}
            </button>
          ) : canModify ? (
            <button
              onClick={(e) => { e.stopPropagation(); setEditingDate(true) }}
              className="flex items-center gap-1 text-[10px] font-body font-medium text-[#6366F1] hover:text-[#4338CA] border border-dashed border-[#C7D2FE] hover:border-[#6366F1] px-2 py-1 rounded-lg transition-colors"
            >
              <CalendarClock className="w-3 h-3" /> Set deadline
            </button>
          ) : (
            <span className="text-[10px] text-[#C0BDB6] font-body">Tanpa deadline</span>
          )}

          <span className="flex items-center gap-1 text-[10px] text-[#C0BDB6] font-body group-hover:text-[#1A1916] transition-colors flex-shrink-0">
            Buka <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>

        {/* Ketergantungan (connect ke project lain) */}
        <div className="mb-2.5 space-y-1.5" onClick={(e) => e.stopPropagation()}>
          {editingDep ? (
            <select
              autoFocus
              value={project.dependsOn ?? ''}
              onChange={(e) => saveDep(e.target.value)}
              onBlur={() => setEditingDep(false)}
              className="w-full text-[10px] px-2 py-1 bg-[#F8F7F3] border border-[#C8C7BF] rounded-lg font-body text-[#1A1916] focus:outline-none focus:border-[#1A1916] cursor-pointer"
            >
              <option value="">— Tanpa ketergantungan —</option>
              {projects.filter((p) => p.id !== project.id).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          ) : blocker ? (
            <button
              onClick={() => { if (canModify) setEditingDep(true) }}
              title={`Project ini dikerjakan setelah "${blocker.name}" selesai`}
              className={`flex items-center gap-1 max-w-full text-[10px] font-body font-medium text-[#4338CA] bg-[#EEF2FF] border border-[#C7D2FE] px-2 py-1 rounded-lg transition-colors ${canModify ? 'hover:bg-[#E0E7FF]' : 'cursor-default'}`}
            >
              <Link2 className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">Setelah: {blocker.name}</span>
            </button>
          ) : canModify ? (
            <button
              onClick={() => setEditingDep(true)}
              className="flex items-center gap-1 text-[10px] font-body text-[#AEADA8] hover:text-[#6366F1] border border-dashed border-[#E0DFD8] hover:border-[#6366F1] px-2 py-1 rounded-lg transition-colors"
            >
              <Link2 className="w-3 h-3" /> Hubungkan ke project
            </button>
          ) : null}

          {/* Project yang terhubung & bergantung pada ini ("Diikuti") */}
          {dependents.length > 0 && (
            <button
              onClick={() => navigate(`/projects/${dependents[0].id}`)}
              title={`Diikuti oleh: ${dependents.map((d) => d.name).join(', ')} — dikerjakan setelah project ini selesai`}
              className="flex items-center gap-1 max-w-full text-[10px] font-body font-medium text-[#15803D] bg-[#ECFDF5] border border-[#A7F3D0] px-2 py-1 rounded-lg hover:bg-[#D1FAE5] transition-colors"
            >
              <CornerDownRight className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">
                Diikuti: {dependents[0].name}
                {dependents.length > 1 ? ` +${dependents.length - 1}` : ''}
              </span>
            </button>
          )}
        </div>

        {/* footer — all in one line */}
        <div className="pt-2.5 border-t border-[#F4F3F0] flex items-center justify-between gap-1">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            {project.pic ? (
              <><PicAvatar name={project.pic} size="sm" />
              <span className="text-[10px] font-body text-[#6B6860] truncate">{project.pic}</span>
</>
            ) : (
              <span className="text-[10px] text-[#C0BDB6] font-body flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />{formatDate(project.createdAt)}
              </span>
            )}
          </div>
          <HealthPill pct={pct} total={total} inProgress={inProgress} />
        </div>
        {project.pic && (
          <p className="text-[9px] text-[#C0BDB6] font-body mt-1 flex items-center gap-1">
            <CalendarDays className="w-2.5 h-2.5" />{formatDate(project.createdAt)}
          </p>
        )}
      </div>
    </div>
  )
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="bg-white border border-[#E8E7E3] rounded-xl overflow-hidden animate-pulse">
      <div className="h-[3px] bg-[#EEEEE9]" />
      <div className="p-4 space-y-2.5">
        <div className="h-4 bg-[#F4F3F0] rounded w-3/4" />
        <div className="h-3 bg-[#F4F3F0] rounded w-full" />
        <div className="h-1.5 bg-[#F4F3F0] rounded-full mt-4" />
        <div className="h-3 bg-[#F4F3F0] rounded w-1/2 mt-1" />
      </div>
    </div>
  )
}

// ─── ChainCard — kartu untuk rangkaian project yang saling terhubung ──────────
function ChainCard({ members, onOpen }: { members: Project[]; onOpen: () => void }) {
  const { tasks, getEffectiveTaskStatus } = useApp()
  let done = 0, total = 0, active = 0, todo = 0
  for (const m of members) {
    const pt = tasks.filter(t => t.projectId === m.id)
    total  += pt.length
    done   += pt.filter(t => getEffectiveTaskStatus(t.id) === 'done').length
    active += pt.filter(t => getEffectiveTaskStatus(t.id) === 'in-progress').length
    todo   += pt.filter(t => getEffectiveTaskStatus(t.id) === 'not-started').length
  }
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  const root = members[0]
  return (
    <button onClick={onOpen}
      className="text-left bg-white border border-[#E8E7E3] rounded-xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-200 flex flex-col group">
      {/* Strip multi-warna dari tiap project di rantai */}
      <div className="h-[3px] flex">
        {members.slice(0, 8).map((m) => (
          <div key={m.id} className="flex-1" style={{ backgroundColor: m.color }} />
        ))}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-1.5 mb-2">
          <span className="flex items-center gap-1 text-[9px] font-body font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#6366F1]">
            <Layers className="w-3 h-3" /> Rangkaian
          </span>
          <span className="text-[10px] text-[#C0BDB6] font-body">{members.length} project</span>
          <ChevronRight className="w-4 h-4 text-[#C0BDB6] ml-auto group-hover:translate-x-0.5 transition-transform" />
        </div>
        <h3 className="font-display text-[15px] font-bold text-[#1A1916] leading-snug break-words mb-0.5">{root.name}</h3>
        <p className="text-[11px] text-[#8A8A85] font-body mb-3">+{members.length - 1} project terhubung · klik untuk lihat</p>

        <div className="mt-auto">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body">Progress gabungan</span>
            <span className="text-[11px] font-body font-semibold text-[#1A1916]">{pct}%</span>
          </div>
          <div className="h-1.5 bg-[#F0EFE9] rounded-full overflow-hidden flex">
            {done   > 0 && <div style={{ width: `${(done/total)*100}%`,   backgroundColor: '#16A34A' }} />}
            {active > 0 && <div style={{ width: `${(active/total)*100}%`, backgroundColor: '#F59E0B' }} />}
          </div>
          <div className="flex items-center gap-2.5 mt-2 text-[11px] font-display font-bold">
            <span style={{ color: '#16A34A' }}>{done}</span>
            <span style={{ color: '#F59E0B' }}>{active}</span>
            <span style={{ color: '#94A3B8' }}>{todo}</span>
            <span className="ml-auto text-[10px] font-body font-normal text-[#C0BDB6]">{total} task</span>
          </div>
        </div>
      </div>
    </button>
  )
}

// ─── ChainDrawer — panel kanan berisi daftar project dalam rangkaian ──────────
function ChainDrawer({ members: initial, onClose, onUnlink }: {
  members: Project[]
  onClose: () => void
  onUnlink: (projectId: string) => void
}) {
  const { tasks, getEffectiveTaskStatus, getProjectLock } = useApp()
  const [members, setMembers] = useState(initial)
  const stat = (id: string) => {
    const pt = tasks.filter(t => t.projectId === id)
    const d  = pt.filter(t => getEffectiveTaskStatus(t.id) === 'done').length
    return { total: pt.length, pct: pt.length > 0 ? Math.round((d / pt.length) * 100) : 0 }
  }
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-[440px] bg-white shadow-2xl flex flex-col animate-slide-in-right">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#F2F1EC] flex-shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-[#EEF2FF] flex items-center justify-center flex-shrink-0">
              <Layers className="w-4 h-4 text-[#6366F1]" />
            </div>
            <div className="min-w-0">
              <h2 className="font-display text-[15px] font-bold text-[#1A1916] leading-none">Rangkaian Project</h2>
              <p className="text-[11px] text-[#8A8A85] font-body mt-0.5">{members.length} project terhubung · urut pengerjaan</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#1A1916] transition-colors flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
          {members.length === 0 ? (
            <p className="text-sm text-[#8A8A85] font-body text-center py-10">Semua project sudah dilepas dari rangkaian.</p>
          ) : members.map((p, idx) => {
            const s = stat(p.id)
            const locked = getProjectLock(p.id).locked
            return (
              <div key={p.id} className="border border-[#E8E7E3] rounded-xl p-3.5 hover:border-[#C8C7BF] hover:shadow-sm transition group/item">
                <div className="flex items-center gap-2.5">
                  <span className="w-6 h-6 rounded-full bg-[#1A1916] text-white text-[11px] font-bold font-display flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                  <Link to={`/projects/${p.id}`} onClick={onClose}
                    className="flex-1 min-w-0 text-[13px] font-body font-semibold text-[#1A1916] break-words hover:text-[#6366F1] transition-colors">{p.name}</Link>
                  {locked && <Lock className="w-3 h-3 text-[#C0BDB6] flex-shrink-0" />}
                  <span className="text-[11px] font-body font-semibold text-[#6B6860] flex-shrink-0">{s.pct}%</span>
                </div>
                <div className="mt-2.5 h-1.5 bg-[#F0EFE9] rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${s.pct}%`, backgroundColor: p.color }} />
                </div>
                <div className="flex items-center justify-between gap-2 mt-2">
                  <span className="flex items-center gap-1.5 text-[10px] text-[#AEADA8] font-body min-w-0 truncate">
                    {p.pic ? <><PicAvatar name={p.pic} size="sm" /> {p.pic}</> : 'Tanpa PIC'}
                  </span>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {p.dependsOn && (
                      <button
                        onClick={() => { onUnlink(p.id); setMembers(prev => prev.filter(m => m.id !== p.id)) }}
                        title="Lepas project ini dari rangkaian"
                        className="flex items-center gap-1 text-[10px] font-body font-medium text-[#DC2626] hover:bg-[#FEF2F2] border border-transparent hover:border-[#FECACA] px-1.5 py-0.5 rounded-md transition-colors"
                      >
                        <Unlink className="w-3 h-3" /> Lepas
                      </button>
                    )}
                    <Link to={`/projects/${p.id}`} onClick={onClose}
                      className="text-[10px] text-[#C0BDB6] hover:text-[#1A1916] font-body transition-colors">{s.total} task · Buka →</Link>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── HomePage ─────────────────────────────────────────────────────────────────
export default function HomePage() {
  const { projects, tasks, subtasks, loading, error, getEffectiveTaskStatus, getProjectOrder, updateProject } = useApp()
  const [searchParams] = useSearchParams()
  const { data: session } = useSession()
  const holding = hasCrossDeptView(session?.user ?? {})
  const [selectedDept, setSelectedDept] = useState<string | null>(null)
  // Tab unit: hanya departemen yang benar-benar punya project, diurutkan induk → divisi
  // internal → unit bisnis, supaya barisnya rapi dan tidak penuh unit kosong. Project yang
  // didelegasi ikut terhitung di departemen tujuannya, selaras dengan filter di bawah.
  const { deptTabs, deptCounts } = useMemo(() => {
    const byDept: Record<string, number> = {}
    for (const p of projects) {
      if (p.department) byDept[p.department] = (byDept[p.department] ?? 0) + 1
      if (p.assignedDepartment && p.assignedDepartment !== p.department) {
        byDept[p.assignedDepartment] = (byDept[p.assignedDepartment] ?? 0) + 1
      }
    }
    const order: string[] = ['HOLDING', ...INTERNAL_DIVISIONS, ...BUSINESS_UNITS]
    const known = new Set<string>(DEPARTMENTS)
    const tabs = Object.keys(byDept)
      .filter((d) => known.has(d))
      .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    return { deptTabs: tabs, deptCounts: { total: projects.length, byDept } }
  }, [projects])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [openChain, setOpenChain] = useState<Project[] | null>(null)
  const [sort, setSort] = useState<SortKey>('recent')
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all')
  const [search, setSearch] = useState('')
  const [showSortMenu, setShowSortMenu] = useState(false)
  const [boardView, setBoardView] = useState<'cards' | 'diagram'>('cards')
  const [onlyLinked, setOnlyLinked] = useState(false) // hanya project yang punya korelasi/hubungan
  const [selectedPic, setSelectedPic] = useState<string | null>(searchParams.get('pic'))
  const [showPicMenu, setShowPicMenu] = useState(false)
  const picMenuRef = useRef<HTMLDivElement>(null)
  useClickOutside(picMenuRef, () => setShowPicMenu(false), showPicMenu)
  const { users: registeredUsers } = useRegisteredUsers()

  // Task RUTIN (repetitif) dikecualikan dari semua perhitungan progres.
  const countableTasks = useMemo(() => tasks.filter(t => !t.recurrence), [tasks])
  const totalTasks = countableTasks.length
  const doneTasks   = countableTasks.filter(t => getEffectiveTaskStatus(t.id) === 'done').length
  const activeTasks = countableTasks.filter(t => getEffectiveTaskStatus(t.id) === 'in-progress').length
  const todoTasks   = countableTasks.filter(t => getEffectiveTaskStatus(t.id) === 'not-started').length
  const globalPct   = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0

  // Seseorang dianggap "terlibat" di sebuah project bila ia PIC project,
  // ATAU assignee salah satu task, ATAU assignee salah satu subtask di project itu.
  // Ini menyamakan konsep dengan panel Beban Kerja Tim di dashboard, supaya saat
  // diklik (mis. kolaborator yang bukan PIC) projectnya tetap muncul.
  const matchPic = useCallback((p: Project, name: string): boolean => {
    const target = name.trim().toLowerCase()
    const pt = tasks.filter(t => t.projectId === p.id)
    const taskIds = new Set(pt.map(t => t.id))
    const subAssignees = subtasks.filter(s => taskIds.has(s.taskId)).flatMap(s => parsePics(s.assignee))
    if (name === 'Unassigned') {
      // 'Unassigned' = project tanpa PIC dan tanpa assignee task/subtask sama sekali
      const hasAnyone =
        (p.pic ?? '').trim() !== '' ||
        pt.some(t => parsePics(t.assignee).length > 0) ||
        subAssignees.some(a => a !== '')
      return !hasAnyone
    }
    return (
      (p.pic ?? '').trim().toLowerCase() === target ||
      pt.some(t => isPic(t.assignee, target)) ||
      subAssignees.some(a => a.toLowerCase() === target)
    )
  }, [tasks, subtasks])

  // Statistik per orang berdasarkan KETERLIBATAN (PIC / assignee task / assignee subtask).
  // Satu project bisa dihitung untuk beberapa orang yang terlibat di dalamnya.
  const picStats = useMemo(() => {
    const map = new Map<string, { name: string; projectCount: number; done: number; active: number; todo: number }>()
    const bump = (display: string, done: number, active: number, todo: number) => {
      const key = display.toLowerCase()
      if (!map.has(key)) map.set(key, { name: display, projectCount: 0, done: 0, active: 0, todo: 0 })
      const e = map.get(key)!
      e.projectCount += 1; e.done += done; e.active += active; e.todo += todo
    }
    for (const p of projects) {
      const pt = tasks.filter(t => t.projectId === p.id)
      const taskIds = new Set(pt.map(t => t.id))
      const done   = pt.filter(t => getEffectiveTaskStatus(t.id) === 'done').length
      const active = pt.filter(t => getEffectiveTaskStatus(t.id) === 'in-progress').length
      const todo   = pt.filter(t => getEffectiveTaskStatus(t.id) === 'not-started').length
      // Kumpulkan orang terlibat (dedupe case-insensitive, simpan display pertama)
      const people = new Map<string, string>()
      const addPerson = (n?: string | null) => {
        const v = (n ?? '').trim()
        if (v && !people.has(v.toLowerCase())) people.set(v.toLowerCase(), v)
      }
      addPerson(p.pic)
      pt.forEach(t => parsePics(t.assignee).forEach(addPerson))
      subtasks.forEach(s => { if (taskIds.has(s.taskId)) parsePics(s.assignee).forEach(addPerson) })
      if (people.size === 0) { bump('Unassigned', done, active, todo); continue }
      people.forEach(display => bump(display, done, active, todo))
    }
    return Array.from(map.values())
      .map(s => ({ ...s, total: s.done + s.active + s.todo }))
      .sort((a, b) => b.projectCount - a.projectCount)
  }, [projects, tasks, subtasks, getEffectiveTaskStatus])

  // Opsi dropdown filter PIC: nama user terdaftar (data/users.json) digabung
  // dengan PIC yang sudah dipakai di project, plus jumlah project per PIC.
  const picOptions = useMemo(() => {
    const counts = new Map(picStats.map(s => [s.name, s.projectCount]))
    const names = new Set<string>()
    registeredUsers.forEach(u => names.add(u.username))
    picStats.forEach(s => { if (s.name !== 'Unassigned') names.add(s.name) })
    const list = Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({ name, count: counts.get(name) ?? 0 }))
    const unassigned = counts.get('Unassigned') ?? 0
    return { list, unassigned }
  }, [registeredUsers, picStats])

  const filterCounts = useMemo(() => {
    const base = selectedPic ? projects.filter(p => matchPic(p, selectedPic)) : projects
    return {
      all: base.length,
      active: base.filter(p => tasks.some(t => t.projectId === p.id && getEffectiveTaskStatus(t.id) === 'in-progress')).length,
      completed: base.filter(p => { const pt = tasks.filter(t => t.projectId === p.id); return pt.length > 0 && pt.every(t => getEffectiveTaskStatus(t.id) === 'done') }).length,
      'not-started': base.filter(p => { const pt = tasks.filter(t => t.projectId === p.id); return pt.length === 0 || pt.every(t => getEffectiveTaskStatus(t.id) === 'not-started') }).length,
    }
  }, [projects, tasks, selectedPic, getEffectiveTaskStatus, matchPic])

  const visibleProjects = useMemo(() => {
    let list = [...projects]
    if (holding && selectedDept) {
      list = list.filter(p => p.department === selectedDept || p.assignedDepartment === selectedDept)
    }
    if (selectedPic) {
      list = list.filter(p => matchPic(p, selectedPic))
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(p => p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q))
    }
    if (filterStatus !== 'all') {
      list = list.filter(p => {
        const pt = tasks.filter(t => t.projectId === p.id)
        if (filterStatus === 'active') return pt.some(t => getEffectiveTaskStatus(t.id) === 'in-progress')
        if (filterStatus === 'completed') return pt.length > 0 && pt.every(t => getEffectiveTaskStatus(t.id) === 'done')
        if (filterStatus === 'not-started') return pt.length === 0 || pt.every(t => getEffectiveTaskStatus(t.id) === 'not-started')
        return true
      })
    }
    if (onlyLinked) {
      // hanya yang punya korelasi: punya "Setelah" (predecessor ada) atau diikuti project lain
      list = list.filter(p =>
        (p.dependsOn && projects.some(x => x.id === p.dependsOn)) ||
        projects.some(x => x.dependsOn === p.id),
      )
    }
    list.sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name)
      if (sort === 'progress') {
        const g = (id: string) => { const pt = tasks.filter(t => t.projectId === id); return pt.length > 0 ? pt.filter(t => getEffectiveTaskStatus(t.id) === 'done').length / pt.length : 0 }
        return g(b.id) - g(a.id)
      }
      if (sort === 'tasks') return tasks.filter(t => t.projectId === b.id).length - tasks.filter(t => t.projectId === a.id).length
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
    return list
  }, [projects, tasks, search, filterStatus, sort, selectedPic, onlyLinked, getEffectiveTaskStatus, holding, selectedDept, matchPic])

  // Rantai dependency: gabungkan project yang saling terhubung (union-find)
  const projectChains = useMemo(() => {
    const parent = new Map<string, string>()
    const find = (x: string): string => { let r = x; while (parent.get(r) !== r) r = parent.get(r)!; return r }
    projects.forEach(p => parent.set(p.id, p.id))
    projects.forEach(p => {
      if (p.dependsOn && parent.has(p.dependsOn)) {
        const a = find(p.id), b = find(p.dependsOn)
        if (a !== b) parent.set(a, b)
      }
    })
    const groups = new Map<string, Project[]>()
    const chainOf = new Map<string, string>()
    projects.forEach(p => {
      const root = find(p.id)
      chainOf.set(p.id, root)
      if (!groups.has(root)) groups.set(root, [])
      groups.get(root)!.push(p)
    })
    return { groups, chainOf }
  }, [projects])

  // Item yang ditampilkan: project tunggal, atau 1 kartu rangkaian (≥2 project terhubung)
  type DisplayItem =
    | { type: 'single'; project: Project }
    | { type: 'chain'; key: string; members: Project[] }
  const displayItems = useMemo<DisplayItem[]>(() => {
    const { groups, chainOf } = projectChains
    const seen = new Set<string>()
    const items: DisplayItem[] = []
    for (const p of visibleProjects) {
      const root = chainOf.get(p.id) ?? p.id
      const members = groups.get(root) ?? [p]
      if (members.length <= 1) { items.push({ type: 'single', project: p }); continue }
      if (seen.has(root)) continue
      seen.add(root)
      const ordered = [...members].sort((a, b) => (getProjectOrder(a.id) ?? 999) - (getProjectOrder(b.id) ?? 999))
      items.push({ type: 'chain', key: root, members: ordered })
    }
    return items
  }, [visibleProjects, projectChains, getProjectOrder])

  if (error) return (
    <div className="min-h-screen bg-[#F5F4F0] flex items-center justify-center p-6">
      <div className="bg-white border border-[#FECACA] rounded-2xl p-6 max-w-md w-full">
        <p className="text-xs font-semibold text-[#DC2626] uppercase tracking-wider mb-2 font-body">Koneksi Gagal</p>
        <p className="text-sm text-[#1A1916] font-body mb-3">{error}</p>
        <a href="/api/health" target="_blank" className="text-xs text-[#2563EB] font-body underline">Cek /api/health →</a>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-[#F5F4F0]">

      {/* HEADER */}
      <header className="bg-white border-b border-[#E8E7E3] sticky top-0 z-30">
        <div className="max-w-[1440px] mx-auto px-6 flex items-center gap-4" style={{ height: '52px' }}>
          <span className="font-display text-[15px] font-bold text-[#1A1916] flex-shrink-0">Project Management</span>
          <div className="flex-1 max-w-sm mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#C0BDB6] pointer-events-none" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Cari project..." className="w-full pl-9 pr-8 py-1.5 bg-[#F4F3F0] border border-transparent rounded-xl text-sm font-body text-[#1A1916] placeholder:text-[#C0BDB6] focus:outline-none focus:bg-white focus:border-[#E0DFD8] transition-all" />
            {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#AEADA8]"><X className="w-3.5 h-3.5" /></button>}
          </div>
          <button onClick={() => setShowCreateModal(true)}
            className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2E2E2A] active:scale-[0.98] transition-all">
            <Plus className="w-3.5 h-3.5" /> Buat Project
          </button>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 py-5 space-y-4">

        {/* ── COMPACT INSIGHT PANEL ── */}
        <div className="bg-white border border-[#E8E7E3] rounded-2xl p-5">

          {/* Row: stats + donut */}
          <div className="flex items-center gap-6">

            {/* Stats */}
            <div className="flex items-center gap-5 flex-1 flex-wrap">
              {[
                { label: 'Projects', value: projects.length, color: '#6366F1' },
                { label: 'Tasks',    value: totalTasks,       color: 'var(--kpi-neutral)' },
                { label: 'Subtasks', value: subtasks.length,  color: '#8B5CF6' },
                { label: 'Active',   value: activeTasks,      color: '#F59E0B' },
                { label: 'Done',     value: doneTasks,        color: '#16A34A' },
                { label: 'Todo',     value: todoTasks,        color: '#94A3B8' },
              ].map((s, i, arr) => (
                <div key={s.label} className="flex items-center gap-5">
                  <div className="text-center">
                    <p className="font-display font-bold leading-none" style={{ fontSize: '1.75rem', color: s.color }}>
                      {loading ? '—' : s.value}
                    </p>
                    <p className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body mt-0.5">{s.label}</p>
                  </div>
                  {i < arr.length - 1 && <div className="w-px h-7 bg-[#F0EFE9]" />}
                </div>
              ))}
            </div>

            {/* Donut + legend */}
            <div className="flex items-center gap-3 pl-5 border-l border-[#F0EFE9] flex-shrink-0">
              <DonutChart done={doneTasks} inProgress={activeTasks} notStarted={todoTasks} />
              <div className="space-y-1">
                {[
                  { color: '#16A34A', label: 'Done',   val: doneTasks },
                  { color: '#F59E0B', label: 'Active', val: activeTasks },
                  { color: '#E2E8F0', label: 'Todo',   val: todoTasks },
                ].map(r => (
                  <div key={r.label} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: r.color }} />
                      <span className="text-[10px] text-[#8A8A85] font-body">{r.label}</span>
                    </div>
                    <span className="text-[10px] font-body font-bold text-[#1A1916]">{r.val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Overall bar */}
          <div className="mt-4 mb-1">
            <div className="flex justify-between mb-1.5">
              <span className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body">Overall Completion</span>
              <span className="text-[10px] font-body font-semibold text-[#6B6860]">{globalPct}%</span>
            </div>
            <div className="h-1.5 bg-[#F0EFE9] rounded-full overflow-hidden flex">
              {doneTasks > 0 && <div className="h-full" style={{ width: `${(doneTasks/totalTasks)*100}%`, backgroundColor: '#16A34A' }} />}
              {activeTasks > 0 && <div className="h-full" style={{ width: `${(activeTasks/totalTasks)*100}%`, backgroundColor: '#F59E0B' }} />}
            </div>
          </div>

          {/* PIC filter — dropdown dari user terdaftar */}
          {(picOptions.list.length > 0 || picOptions.unassigned > 0) && (
            <div className="flex items-center gap-2 flex-wrap pt-3 mt-2 border-t border-[#F4F3F0]">
              <span className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body">PIC</span>
              <div ref={picMenuRef} className="relative">
                <button
                  onClick={() => setShowPicMenu(o => !o)}
                  aria-haspopup="listbox"
                  aria-expanded={showPicMenu}
                  className="flex items-center gap-2 px-3 py-1.5 bg-[#F8F7F3] border border-[#E8E7E3] rounded-xl hover:border-[#C8C7BF] transition-colors"
                >
                  {selectedPic
                    ? (selectedPic === 'Unassigned'
                        ? <User className="w-3.5 h-3.5 text-[#C0BDB6]" />
                        : <PicAvatar name={selectedPic} size="sm" />)
                    : <User className="w-3.5 h-3.5 text-[#AEADA8]" />}
                  <span className="text-[11px] font-body font-medium text-[#1A1916]">{selectedPic ?? 'Semua PIC'}</span>
                  <ChevronDown className={`w-3 h-3 text-[#AEADA8] transition-transform ${showPicMenu ? 'rotate-180' : ''}`} />
                </button>

                {showPicMenu && (
                  <div role="listbox" className="absolute left-0 top-[calc(100%+4px)] z-20 min-w-[220px] max-h-64 overflow-auto bg-white border border-[#E8E7E3] rounded-xl shadow-xl py-1.5 animate-fade-in">
                    <button
                      type="button" role="option" aria-selected={!selectedPic}
                      onClick={() => { setSelectedPic(null); setShowPicMenu(false) }}
                      className={`w-full flex items-center gap-2 px-3.5 py-2 text-xs font-body transition-colors ${!selectedPic ? 'bg-[#F4F3F0] text-[#1A1916] font-semibold' : 'text-[#6B6860] hover:bg-[#F4F3F0]'}`}
                    >
                      <span className="w-5 h-5 rounded-full bg-[#EDECE7] flex items-center justify-center flex-shrink-0"><User className="w-3 h-3 text-[#AEADA8]" /></span>
                      <span className="flex-1 text-left">Semua PIC</span>
                      {!selectedPic && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                    </button>

                    {picOptions.list.map(opt => {
                      const active = selectedPic === opt.name
                      return (
                        <button
                          key={opt.name} type="button" role="option" aria-selected={active}
                          onClick={() => { setSelectedPic(opt.name); setShowPicMenu(false) }}
                          className={`w-full flex items-center gap-2 px-3.5 py-2 text-xs font-body transition-colors ${active ? 'bg-[#F4F3F0] text-[#1A1916] font-semibold' : 'text-[#6B6860] hover:bg-[#F4F3F0]'}`}
                        >
                          <PicAvatar name={opt.name} size="sm" />
                          <span className="flex-1 text-left truncate">{opt.name}</span>
                          <span className="text-[10px] text-[#C0BDB6]">{opt.count} proj</span>
                          {active && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                        </button>
                      )
                    })}

                    {picOptions.unassigned > 0 && (
                      <button
                        type="button" role="option" aria-selected={selectedPic === 'Unassigned'}
                        onClick={() => { setSelectedPic('Unassigned'); setShowPicMenu(false) }}
                        className={`w-full flex items-center gap-2 px-3.5 py-2 text-xs font-body transition-colors ${selectedPic === 'Unassigned' ? 'bg-[#F4F3F0] text-[#1A1916] font-semibold' : 'text-[#6B6860] hover:bg-[#F4F3F0]'}`}
                      >
                        <span className="w-5 h-5 rounded-full bg-[#EDECE7] flex items-center justify-center flex-shrink-0"><User className="w-3 h-3 text-[#C0BDB6]" /></span>
                        <span className="flex-1 text-left">Unassigned</span>
                        <span className="text-[10px] text-[#C0BDB6]">{picOptions.unassigned} proj</span>
                        {selectedPic === 'Unassigned' && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {selectedPic && (
                <button
                  onClick={() => setSelectedPic(null)}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-body text-[#AEADA8] hover:text-[#1A1916] transition-colors"
                >
                  <X className="w-3 h-3" /> Reset
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── PIC FOCUS PANEL ── */}
        {selectedPic && (() => {
          const pic = picStats.find(p => p.name.toLowerCase() === selectedPic.trim().toLowerCase())
          if (!pic) return null
          const pct = pic.total > 0 ? Math.round((pic.done / pic.total) * 100) : 0
          const col = selectedPic === 'Unassigned' ? '#C0BDB6' : picColor(selectedPic)
          return (
            <div className="bg-white border-2 rounded-2xl p-5 flex items-center gap-6" style={{ borderColor: col }}>
              {/* Avatar large */}
              {selectedPic !== 'Unassigned'
                ? <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold font-body text-lg flex-shrink-0" style={{ backgroundColor: col }}>
                    {selectedPic.charAt(0).toUpperCase()}
                  </div>
                : <div className="w-12 h-12 rounded-full bg-[#F4F3F0] border border-[#E8E7E3] flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-[#C0BDB6]" />
                  </div>
              }

              {/* Name + project count */}
              <div className="flex-shrink-0">
                <p className="font-display text-xl font-bold text-[#1A1916]">{selectedPic}</p>
                <p className="text-[11px] text-[#C0BDB6] font-body">{pic.projectCount} project</p>
              </div>

              <div className="w-px h-10 bg-[#F0EFE9] flex-shrink-0" />

              {/* Stats */}
              <div className="flex items-center gap-6">
                {[
                  { label: 'Done',   val: pic.done,   color: '#16A34A' },
                  { label: 'Aktif',  val: pic.active, color: '#F59E0B' },
                  { label: 'Todo',   val: pic.todo,   color: '#94A3B8' },
                ].map(s => (
                  <div key={s.label} className="text-center">
                    <p className="font-display text-2xl font-bold leading-none" style={{ color: s.color }}>{s.val}</p>
                    <p className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              <div className="w-px h-10 bg-[#F0EFE9] flex-shrink-0" />

              {/* Progress */}
              <div className="flex-1 min-w-0">
                <div className="flex justify-between mb-1.5">
                  <span className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body">Completion</span>
                  <span className="text-sm font-display font-bold" style={{ color: col }}>{pct}%</span>
                </div>
                <div className="h-2 bg-[#F0EFE9] rounded-full overflow-hidden flex">
                  {pic.done > 0 && <div style={{ width: `${(pic.done/pic.total)*100}%`, backgroundColor: '#16A34A' }} className="h-full" />}
                  {pic.active > 0 && <div style={{ width: `${(pic.active/pic.total)*100}%`, backgroundColor: '#F59E0B' }} className="h-full" />}
                </div>
                <p className="text-[10px] text-[#A8A8A2] font-body mt-1">
                  Menampilkan {visibleProjects.length} project milik {selectedPic}
                </p>
              </div>

              {/* Clear button */}
              <button
                onClick={() => setSelectedPic(null)}
                className="flex-shrink-0 p-2 rounded-xl hover:bg-[#F4F3F0] text-[#C0BDB6] hover:text-[#1A1916] transition-colors"
                title="Kembali ke semua project"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )
        })()}

        {/* ── TAB UNIT — hanya untuk yang punya pandangan lintas unit (HOLDING Head+ /
             Director). Anggota divisi atau unit bisnis lain cuma melihat unitnya sendiri,
             jadi barisnya tidak dirender sama sekali. ── */}
        {holding && (
          <div className="flex items-center gap-1 overflow-x-auto pb-1 -mb-1">
            <button
              onClick={() => setSelectedDept(null)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-body font-medium border transition-colors ${
                selectedDept === null
                  ? 'bg-[#1A1916] text-white border-[#1A1916]'
                  : 'bg-white text-[#8A8A85] border-[#E8E7E3] hover:text-[#1A1916] hover:border-[#C8C7BF]'
              }`}
            >
              Semua
              <span className={`ml-1.5 text-[10px] ${selectedDept === null ? 'opacity-50' : 'text-[#C8C7BF]'}`}>
                {deptCounts.total}
              </span>
            </button>
            {deptTabs.map((d) => {
              const active = selectedDept === d
              const c = deptColor(d)
              return (
                <button
                  key={d}
                  onClick={() => setSelectedDept(active ? null : d)}
                  title={`Project ${d}`}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-body font-medium border transition-colors ${
                    active ? 'text-white' : 'bg-white hover:border-[#C8C7BF]'
                  }`}
                  style={
                    active
                      ? { backgroundColor: c.text, borderColor: c.text }
                      : { color: c.text, backgroundColor: c.bg, borderColor: c.border }
                  }
                >
                  {d}
                  <span className={`ml-1.5 text-[10px] ${active ? 'opacity-60' : 'opacity-50'}`}>
                    {deptCounts.byDept[d] ?? 0}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {/* ── FILTER + TOOLBAR ── */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1 bg-white border border-[#E8E7E3] rounded-xl p-1">
            {FILTER_TABS.map(tab => (
              <button key={tab.key} onClick={() => setFilterStatus(tab.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-body font-medium transition-all ${filterStatus === tab.key ? 'bg-[#1A1916] text-white' : 'text-[#8A8A85] hover:text-[#1A1916]'}`}>
                {tab.label}
                <span className={`ml-1 text-[10px] ${filterStatus === tab.key ? 'opacity-50' : 'text-[#C8C7BF]'}`}>{filterCounts[tab.key]}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {search && <span className="text-xs text-[#A8A8A2] font-body">{visibleProjects.length} hasil</span>}

            {/* Filter: hanya project yang berkorelasi (punya hubungan) */}
            <button
              onClick={() => setOnlyLinked((v) => !v)}
              title="Tampilkan hanya project yang punya korelasi/hubungan"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-body font-medium border transition-colors ${
                onlyLinked
                  ? 'bg-[#1A1916] text-white border-[#1A1916]'
                  : 'bg-white text-[#6B6860] border-[#E8E7E3] hover:border-[#C8C7BF]'
              }`}
            >
              <Link2 className="w-3.5 h-3.5" /> Berkorelasi
            </button>

            {/* Toggle tampilan: Kartu / Diagram (ERD kabel) */}
            <div className="flex items-center gap-0.5 bg-white border border-[#E8E7E3] rounded-xl p-0.5">
              <button onClick={() => setBoardView('cards')} title="Tampilan kartu"
                className={`flex items-center justify-center w-7 h-7 rounded-lg transition-colors ${boardView === 'cards' ? 'bg-[#1A1916] text-white' : 'text-[#8A8A85] hover:text-[#1A1916]'}`}>
                <LayoutGrid className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setBoardView('diagram')} title="Tampilan diagram (kabel/ERD)"
                className={`flex items-center justify-center w-7 h-7 rounded-lg transition-colors ${boardView === 'diagram' ? 'bg-[#1A1916] text-white' : 'text-[#8A8A85] hover:text-[#1A1916]'}`}>
                <Network className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="relative">
              <button onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E8E7E3] rounded-xl text-xs font-body text-[#6B6860] hover:border-[#C8C7BF] transition-colors">
                <ArrowUpDown className="w-3 h-3" />
                {SORT_OPTIONS.find(s => s.key === sort)?.label}
                <ChevronDown className="w-3 h-3 opacity-40" />
              </button>
              {showSortMenu && (<>
                <div className="fixed inset-0 z-10" onClick={() => setShowSortMenu(false)} />
                <div className="absolute right-0 top-9 z-20 bg-white border border-[#E8E7E3] rounded-xl shadow-xl py-1.5 w-40 animate-fade-in">
                  {SORT_OPTIONS.map(s => (
                    <button key={s.key} onClick={() => { setSort(s.key); setShowSortMenu(false) }}
                      className={`w-full flex items-center justify-between px-3.5 py-2 text-xs font-body transition-colors ${sort === s.key ? 'text-[#1A1916] font-semibold bg-[#F4F3F0]' : 'text-[#6B6860] hover:bg-[#F4F3F0]'}`}>
                      {s.label}{sort === s.key && <span>✓</span>}
                    </button>
                  ))}
                </div>
              </>)}
            </div>

          </div>
        </div>

        {/* ── PROJECTS (Kartu / Diagram) ── */}
        {boardView === 'diagram' ? (
              <ProjectDiagram onlyLinked={onlyLinked} />
            ) : loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {[...Array(5)].map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : visibleProjects.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-12 h-12 rounded-2xl bg-white border border-[#E8E7E3] flex items-center justify-center mb-3">
                  {search ? <Search className="w-5 h-5 text-[#C0BDB6]" /> : <FolderOpen className="w-5 h-5 text-[#C0BDB6]" />}
                </div>
                <h3 className="font-display text-xl font-bold text-[#1A1916] mb-1.5">
                  {search ? 'Tidak ada hasil' : 'Belum ada project'}
                </h3>
                <p className="text-sm text-[#8A8A85] font-body mb-5 max-w-xs">
                  {search ? `Tidak ada yang cocok dengan "${search}"` : 'Buat project pertama untuk mulai tracking'}
                </p>
                {!search && (
                  <button onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2E2E2A]">
                    <Plus className="w-4 h-4" /> Buat Project
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {displayItems.map(item =>
                  item.type === 'single'
                    ? <ProjectCard key={item.project.id} project={item.project} />
                    : <ChainCard key={item.key} members={item.members} onOpen={() => setOpenChain(item.members)} />
                )}
              </div>
        )}
      </main>

      {openChain && (
        <ChainDrawer
          members={openChain}
          onClose={() => setOpenChain(null)}
          onUnlink={(id) => updateProject(id, { dependsOn: null })}
        />
      )}
      {showCreateModal && <CreateProjectModal onClose={() => setShowCreateModal(false)} />}
    </div>
  )
}
